import type {
  StrategyClassicStripId,
  StrategyConnectivityContext,
  StrategyMapCardId,
} from './strategyConnectivityTypes';

export interface StrategyConnectivityHelp {
  pageTitle: string;
  bullets: string[];
  mapHighlightId: StrategyMapCardId | null;
  classicHighlightId: StrategyClassicStripId | null;
}

const HELP: Record<StrategyConnectivityContext, StrategyConnectivityHelp> = {
  strategy_objects_guide: {
    pageTitle: 'Guided strategy objects',
    bullets: [
      'This walkthrough follows the canonical clarity path: Outcome Driver → Desired Outcome, then classic alignment (Strategy → Objectives → Goals).',
      'Back Plan and Opportunity Solution Tree branch after a desired outcome; open questions can surface at any time. Each step screen has How this connects for the live methodology map.',
    ],
    mapHighlightId: null,
    classicHighlightId: null,
  },
  strategy_hub: {
    pageTitle: 'Strategy hub',
    bullets: [
      'This page is the front door to every framework: classic alignment (Strategy → Objectives → Goals), Opportunity Solution Tree, Strategy Kernel, Strategy Canvas, and clarity artifacts (drivers, desired outcomes, back plans, open questions).',
      'Use the map to see how “why” flows into discovery and execution, and how everything can trace into prioritization items.',
    ],
    mapHighlightId: null,
    classicHighlightId: null,
  },
  strategy_list: {
    pageTitle: 'Strategies (classic)',
    bullets: [
      'A Strategy is the top container in classic alignment: vision, mission, north star, and drivers live on each strategy row.',
      'Objectives and Goals hang under a strategy. Outcome drivers can be linked from a strategy detail page. Clarity artifacts (OST, back plan, etc.) connect to items independently via methodology links.',
    ],
    mapHighlightId: null,
    classicHighlightId: 'strategies',
  },
  strategy_detail: {
    pageTitle: 'Strategy detail',
    bullets: [
      'You are editing one workspace Strategy. Objectives and goals created here belong to this container.',
      'You can link outcome drivers to this strategy. Drivers still participate in the global driver → desired outcome chain on the map.',
    ],
    mapHighlightId: null,
    classicHighlightId: 'strategies',
  },
  strategy_create: {
    pageTitle: 'New strategy',
    bullets: [
      'Creating a strategy adds a classic alignment container. After save, define objectives and goals, and optionally link outcome drivers from the detail page.',
    ],
    mapHighlightId: null,
    classicHighlightId: 'strategies',
  },
  objectives: {
    pageTitle: 'Strategic objectives',
    bullets: [
      'Objectives sit under a Strategy and express directional outcomes for alignment and reporting.',
      'They connect downstream to Goals and to prioritization work (cycles, items, alignment views). They are not the same thing as “desired outcomes” in the clarity path — those live on the driver → outcome chain.',
    ],
    mapHighlightId: null,
    classicHighlightId: 'objectives',
  },
  goals: {
    pageTitle: 'Goals',
    bullets: [
      'Goals refine objectives into concrete targets. They inherit strategy context and feed alignment and prioritization views.',
    ],
    mapHighlightId: null,
    classicHighlightId: 'goals',
  },
  alignment: {
    pageTitle: 'Strategy alignment',
    bullets: [
      'Alignment inspects how priorities and items map to objectives (classic model). It answers whether you are funding the strategy.',
      'Use it together with clarity artifacts: methodology connections from drivers, back plans, OST nodes, and similar links show why work exists on items.',
    ],
    mapHighlightId: null,
    classicHighlightId: 'alignment',
  },
  decisions: {
    pageTitle: 'Decision history',
    bullets: [
      'Decision history is the governance memory of prioritization (lists, overrides, sessions, etc.). It is not drawn on the methodology map, but it consumes the outputs of cycles that strategies and goals align to.',
    ],
    mapHighlightId: null,
    classicHighlightId: 'decisions',
  },
  opportunity_trees_list: {
    pageTitle: 'Opportunity solution trees',
    bullets: [
      'Each tree explores outcomes → opportunities → solutions → experiments (Teresa Torres). The root outcome is a product outcome, often aligned with desired outcome thinking.',
      'Solution (and other) nodes can link to prioritization items so discovery connects to delivery tracking.',
    ],
    mapHighlightId: 'ost',
    classicHighlightId: null,
  },
  ost_editor: {
    pageTitle: 'Opportunity solution tree editor',
    bullets: [
      'You are shaping discovery on one tree. Experiments and solutions tie back to evidence; linking nodes to items makes tradeoffs visible in the item record (Classification → Clarity context).',
    ],
    mapHighlightId: 'ost',
    classicHighlightId: null,
  },
  kernel_list: {
    pageTitle: 'Strategy kernels',
    bullets: [
      'Kernel strategy (Rumelt) organizes Diagnosis, Guiding Policy, and Coherent Action as a separate lens from classic Strategy, Objective, and Goal.',
      'It complements clarity work: same workspace can use kernel for narrative discipline and classic rows for portfolio alignment.',
    ],
    mapHighlightId: null,
    classicHighlightId: 'kernel',
  },
  kernel_detail: {
    pageTitle: 'Strategy kernel detail',
    bullets: [
      'This artifact is one kernel document. It does not replace objectives/goals; it adds a coherent diagnosis/policy/action story.',
    ],
    mapHighlightId: null,
    classicHighlightId: 'kernel',
  },
  canvas_list: {
    pageTitle: 'Strategy canvases',
    bullets: [
      'A Strategy Canvas is a one-page narrative: vision, purpose, mission, principles, competencies, assumptions — linked to strategies, objectives, and goals for communication.',
    ],
    mapHighlightId: null,
    classicHighlightId: 'canvas',
  },
  canvas_editor: {
    pageTitle: 'Strategy canvas editor',
    bullets: [
      'You are editing one canvas. Linked strategies and inline objectives and goals keep the story tied to live workspace objects.',
    ],
    mapHighlightId: null,
    classicHighlightId: 'canvas',
  },
  outcome_drivers: {
    pageTitle: 'Outcome drivers',
    bullets: [
      'A driver answers why change is needed (context, facts, opportunity, ramifications). It is the start of the canonical methodology chain: Outcome Driver → Desired Outcome.',
      'Drivers can be associated with classic strategies; they still feed desired outcomes and downstream artifacts.',
    ],
    mapHighlightId: 'outcome-driver',
    classicHighlightId: null,
  },
  outcome_driver_new: {
    pageTitle: 'New outcome driver',
    bullets: [
      'Saving here creates a persisted driver — the entry point for the Outcome Driver → Desired Outcome relationship on the map.',
    ],
    mapHighlightId: 'outcome-driver',
    classicHighlightId: null,
  },
  desired_outcomes: {
    pageTitle: 'Desired outcomes',
    bullets: [
      'Desired outcomes capture the seven-question outcome narrative: who, what, evidence, obstacles, steps, etc.',
      'They sit after drivers in parallel with OST and back plan: from here teams branch into backward reasoning (back plan) or continuous discovery (OST).',
    ],
    mapHighlightId: 'desired-outcome',
    classicHighlightId: null,
  },
  desired_outcome_editor: {
    pageTitle: 'Desired outcome detail',
    bullets: [
      'Editing defines the outcome narrative used with OST roots and methodology links. Use Link to item to tie this outcome to prioritization work where it matters.',
    ],
    mapHighlightId: 'desired-outcome',
    classicHighlightId: null,
  },
  backplan_list: {
    pageTitle: 'Back plans',
    bullets: [
      'A back plan works backward from success: conditions, goals as nodes, swimlanes, milestones. It is the parallel path to OST after a desired outcome.',
      'Nodes can link to items via methodology connections.',
    ],
    mapHighlightId: 'backplan',
    classicHighlightId: null,
  },
  backplan_editor: {
    pageTitle: 'Back plan editor',
    bullets: [
      'You are editing one document’s graph. The methodology map treats this as “goal node & backward reasoning” feeding execution evidence on items.',
    ],
    mapHighlightId: 'backplan',
    classicHighlightId: null,
  },
  roadmap_list: {
    pageTitle: 'Roadmaps (Planning)',
    bullets: [
      'Roadmaps reuse the same node / edge / swimlane / milestone model as back plans, framed for forward-chronological product planning.',
      'They live under Planning mode; roadmap nodes can still link to prioritization items via methodology connections.',
    ],
    mapHighlightId: 'roadmap',
    classicHighlightId: null,
  },
  roadmap_editor: {
    pageTitle: 'Roadmap editor',
    bullets: [
      'You are editing one roadmap graph in Planning. Link nodes to items when execution work should trace to this structure.',
    ],
    mapHighlightId: 'roadmap',
    classicHighlightId: null,
  },
  open_questions: {
    pageTitle: 'Open questions',
    bullets: [
      'Open questions are ambient: they can attach to almost any stage and anchor to items, cycles, sessions, or tracks.',
      'They are not sequenced after drivers by default — teams surface unknowns whenever they appear.',
    ],
    mapHighlightId: 'open-questions',
    classicHighlightId: null,
  },
};

export function getStrategyConnectivityHelp(
  context: StrategyConnectivityContext,
): StrategyConnectivityHelp {
  return HELP[context];
}
